import { useState } from "react";
import api, { apiError, brl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Camera, ChevronUp, ChevronDown } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import ProductImages from "@/components/panel/ProductImages";
import BarcodeScanner from "@/components/BarcodeScanner";
import SupplierPicker from "@/components/panel/pickers/SupplierPicker";
import { Button, Checkbox, Field, Input, RichText, Select } from "@/components/ui-kit";
import { descricaoParaEditor } from "@/lib/richText";
import { move } from "./reorder";

/* Rótulo de grupo (<legend>) no mesmo idioma visual do rótulo de campo do kit. */
const LEGEND = "text-eyebrow uppercase tracking-wider text-slate-500";

const EMPTY = {
  name: "", brand: "", category_slug: "", description: "", price: "", cost_price: "", discount_price: "", images: [], featured: false, active: true, rentable: false,
  seo_title: "", seo_description: "", video_url: "", supplier_id: "",
  ncm: "", cest: "", origem: "", unidade: "", ean: "",
  // Tributação de SAÍDA. Vazio = padrão das Configurações fiscais.
  cfop: "", icms_cst: "", icms_aliquota: "", pis_cst: "", pis_aliquota: "", cofins_cst: "", cofins_aliquota: "",
  // Referência da nota de compra. Nunca lida na emissão.
  cfop_entrada: "", icms_cst_entrada: "", icms_aliquota_entrada: "",
  pis_cst_entrada: "", pis_aliquota_entrada: "", cofins_cst_entrada: "", cofins_aliquota_entrada: "",
  weight: "", height: "", width: "", length: "",
  variants: [{ label: "Único", stock: 10, price_delta: 0, barcode: "" }],
};

// normaliza um produto vindo da API para o formato do formulário
const toForm = (p) => ({
  ...EMPTY, ...p,
  // Produto de antes do editor tem texto puro com quebras de linha; entra no
  // editor já em parágrafos, senão abriria tudo numa linha só e pareceria
  // que a troca de editor corrompeu a descrição.
  description: descricaoParaEditor(p.description),
  images: p.images?.length ? p.images.map((im) => im.url) : (p.image ? [p.image] : []),
  variants: (p.variants || EMPTY.variants).map((v) => ({
    id: v.id, label: v.label, price_delta: v.price_delta ?? 0,
    stock: v.stock ?? 0,
    barcode: v.barcode ?? "",
    // Vem do servidor (uma consulta para a lista inteira). É o que deixa a
    // remoção perguntar ANTES, em vez de a gravação falhar depois.
    has_history: !!v.has_history,
  })),
});

const numOrNull = (v) => (v === "" || v == null ? null : parseFloat(v));
const strOrNull = (v) => (v === "" || v == null ? null : v);

export default function ProductForm({ product, categories, brands, onClose, onSaved }) {
  const [form, setForm] = useState(product ? toForm(product) : { ...EMPTY });
  // O nome vem junto do produto (`with('supplier:id,name')`): o seletor busca
  // paginado e o fornecedor vinculado raramente cai na primeira página.
  const [supplierName, setSupplierName] = useState(product?.supplier?.name || "");
  const [saving, setSaving] = useState(false);
  /* Variação com histórico esperando o "sim". `null` quando não há pergunta
     aberta; `{ i, label }` enquanto o diálogo está na tela. */
  const [removendoVariacao, setRemovendoVariacao] = useState(null);
  /* A pessoa já confirmou pelo menos uma remoção com histórico nesta edição —
     o servidor exige o aval junto da gravação, não no clique. */
  const [confirmouHistorico, setConfirmouHistorico] = useState(false);
  const [scanFor, setScanFor] = useState(null); // índice da variação sendo escaneada
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const setVariant = (idx, key, value) => {
    const variants = form.variants.map((v, i) => (i === idx ? { ...v, [key]: value } : v));
    setForm({ ...form, variants });
  };

  /**
   * A ordem da lista É a ordem do cadastro.
   *
   * O servidor grava a posição pela ordem em que estas linhas chegam (ver
   * prepVariants), então mover aqui é mover no PDV, na loja e na etiqueta. É o
   * que resolve o tamanho cadastrado depois — o M que faltava entre o P e o G —
   * caindo no fim da lista de quem está escolhendo com o cliente na frente.
   */
  const moverVariacao = (idx, destino) => setForm({ ...form, variants: move(form.variants, idx, destino) });

  /* Tirar a variação da lista.

     Sem histórico, sai na hora — é o gesto de quem digitou errado e está
     corrigindo, e uma confirmação ali só ensina a clicar em "sim" sem ler.
     Com histórico, pergunta: a variação não pode ser apagada de verdade (a
     venda aconteceu e precisa continuar resolvível), então o que vai acontecer
     é diferente do que o ícone de lixeira promete, e isso tem que ser dito. */
  const removerVariacao = (i) => {
    const v = form.variants[i];
    if (!v?.has_history) { tirarDaLista(i); return; }
    setRemovendoVariacao({ i, label: v.label || `Variação ${i + 1}` });
  };

  const tirarDaLista = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));

  const save = async () => {
    // Espelha o que o backend exige (rules() no ProductAdminController). Sem isso
    // o operador só descobria o campo faltando ao receber o 422 depois de salvar.
    if (!form.name.trim()) { toast.error("Informe o nome do produto"); return; }
    if (!form.category_slug) { toast.error("Escolha a categoria"); return; }
    if (!form.price) { toast.error("Informe o preço final de venda"); return; }
    if (!form.images.length) { toast.error("Adicione ao menos uma foto"); return; }
    if (!form.variants.length) { toast.error("Cadastre ao menos uma variação"); return; }
    const semRotulo = form.variants.findIndex((v) => !v.label?.trim());
    if (semRotulo >= 0) { toast.error(`Dê um nome à variação ${semRotulo + 1} (ex.: Único, Tamanho M)`); return; }
    setSaving(true);
    const payload = {
      ...form, price: parseFloat(form.price),
      cost_price: numOrNull(form.cost_price), discount_price: numOrNull(form.discount_price),
      brand: strOrNull(form.brand?.trim()),
      supplier_id: strOrNull(form.supplier_id),
      ncm: strOrNull(form.ncm), cest: strOrNull(form.cest),
      origem: strOrNull(form.origem), unidade: strOrNull(form.unidade), ean: strOrNull(form.ean),
      // Saída: vazio vira null de propósito — é o que faz cair no padrão da
      // loja em vez de gravar string vazia, que a SEFAZ recusaria.
      cfop: strOrNull(form.cfop),
      icms_cst: strOrNull(form.icms_cst), icms_aliquota: numOrNull(form.icms_aliquota),
      pis_cst: strOrNull(form.pis_cst), pis_aliquota: numOrNull(form.pis_aliquota),
      cofins_cst: strOrNull(form.cofins_cst), cofins_aliquota: numOrNull(form.cofins_aliquota),
      cfop_entrada: strOrNull(form.cfop_entrada),
      icms_cst_entrada: strOrNull(form.icms_cst_entrada), icms_aliquota_entrada: numOrNull(form.icms_aliquota_entrada),
      pis_cst_entrada: strOrNull(form.pis_cst_entrada), pis_aliquota_entrada: numOrNull(form.pis_aliquota_entrada),
      cofins_cst_entrada: strOrNull(form.cofins_cst_entrada), cofins_aliquota_entrada: numOrNull(form.cofins_aliquota_entrada),
      weight: numOrNull(form.weight), height: numOrNull(form.height), width: numOrNull(form.width), length: numOrNull(form.length),
      variants: form.variants.map((v) => ({ ...v, stock: parseInt(v.stock) || 0, price_delta: parseFloat(v.price_delta) || 0 })),
      // O servidor recusa remover variação com histórico sem este aval — ele é
      // a segunda confirmação, e viaja com a gravação porque é nela que a
      // remoção de fato acontece.
      ...(confirmouHistorico ? { confirm_variant_history: true } : {}),
    };
    try {
      if (product?.id) await api.patch(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product?.id ? "Produto atualizado" : "Produto criado");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <>
    <Modal open onClose={onClose} title={product?.id ? "Editar produto" : "Novo produto"} maxWidth="max-w-2xl" testId="product-form-dialog"
      /* O salvar no RODAPÉ FIXO, não no fim do formulário. São 41 campos: no
         celular o botão ficava a 1.622px de rolagem numa folha de 694px de
         altura visível, e a pessoa precisava percorrer o cadastro inteiro de
         volta para gravar o que acabou de digitar. O Modal já reservava espaço
         para isso. */
      footer={(
        <Button onClick={save} loading={saving} size="lg" block data-testid="product-form-save">
          {saving ? "Salvando..." : "Salvar produto"}
        </Button>
      )}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome do produto" span required>
            <Input value={form.name} onChange={set("name")} placeholder="Ex: Cadeira de Rodas Confort Plus" data-testid="product-form-name" />
          </Field>
          <Field label="Categoria" required>
            <Select value={form.category_slug} onChange={set("category_slug")} data-testid="product-form-category">
              <option value="">Selecione...</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </Select>
          </Field>
          {/* Marca subiu do acordeão fiscal para cá.
              Ela é IDENTIDADE do produto — é por ela que o cliente pergunta no
              balcão ("a muleta da Mercur") — e não dado de nota fiscal. Enterrada
              atrás de "dados fiscais · dimensões", ninguém preenchia, e o filtro
              por marca nasceria vazio. */}
          <Field label="Marca" hint="Quem fabrica. O fornecedor (de quem se compra) fica abaixo.">
            <Input list="marcas-cadastradas" value={form.brand || ""} onChange={set("brand")}
              placeholder="Ex: Mercur, Ortobras, Jaguaribe" data-testid="product-form-brand" maxLength={120} />
            {/* Sugestão a partir do que já existe: sem ela o mesmo fabricante
                vira "Mercur", "mercur" e "Mercur S.A." em três cadastros, e o
                filtro por marca deixa de servir para qualquer coisa. */}
            {/* `value` E conteúdo, com o mesmo texto: o Firefox mostra o
                conteúdo, o Safari mostra o `value`, e o Chrome não duplica
                quando os dois são iguais. Só com `value` a sugestão não aparece
                em parte dos navegadores. */}
            <datalist id="marcas-cadastradas">
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </datalist>
          </Field>
          {/* Fornecedor também saiu do acordeão: junto da marca, o cadastro
              responde de uma vez "quem faz" e "de quem eu compro" — as duas
              perguntas da reposição. */}
          <Field label="Fornecedor" span hint="De quem se compra. Pode mudar sem a marca mudar.">
            <SupplierPicker
              value={form.supplier_id}
              onChange={(id, label) => { setForm({ ...form, supplier_id: id }); setSupplierName(label); }}
              selectedLabel={supplierName}
              testId="product-form-supplier"
            />
          </Field>
          {/* Três preços na mesma linha: o rótulo "Preços (R$)" nomeia o GRUPO,
              não um campo, então é <legend> de <fieldset> — um <label> aqui não
              rotularia nada. A legenda de cada preço fica ABAIXO do campo (é a
              leitura visual que o operador espera nesta grade), então o nome
              acessível de cada um vem por aria-label. */}
          <fieldset className="col-span-2">
            <legend className={LEGEND}>Preços (R$)</legend>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              <div>
                <Input type="number" step="0.01" min="0" value={form.cost_price ?? ""} onChange={set("cost_price")}
                  placeholder="Custo" aria-label="Preço de custo (interno)" data-testid="product-form-cost" mono />
                <span className="block text-[10px] text-slate-400 mt-0.5">Custo (interno)</span>
              </div>
              <div>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={set("price")}
                  placeholder="0,00" aria-label="Preço final de venda" required data-testid="product-form-price"
                  mono className="border-brand-200" />
                <span className="block text-[10px] text-slate-500 font-semibold mt-0.5">Preço final (venda) *</span>
              </div>
              <div>
                <Input type="number" step="0.01" min="0" value={form.discount_price ?? ""} onChange={set("discount_price")}
                  placeholder="Promoção" aria-label="Preço com desconto (opcional)" data-testid="product-form-discount"
                  mono className="border-emerald-200" />
                <span className="block text-[10px] text-emerald-600 mt-0.5">Com desconto (opcional)</span>
              </div>
            </div>
            {(() => {
              const cost = parseFloat(form.cost_price) || 0;
              const disc = parseFloat(form.discount_price) || 0;
              const full = parseFloat(form.price) || 0;
              const sell = disc > 0 && disc < full ? disc : full;
              if (!cost || !sell) return null;
              const lucro = sell - cost;
              const pct = sell > 0 ? (lucro / sell) * 100 : 0;
              return <p className={`text-xs mt-2 font-medium ${lucro >= 0 ? "text-emerald-600" : "text-red-500"}`}>Margem: {brl(lucro)} por venda ({pct.toFixed(0)}% do preço cobrado {brl(sell)}){disc > 0 && disc >= full ? " · desconto ≥ preço final será ignorado" : ""}</p>;
            })()}
          </fieldset>
          {/* Sem `required`: o produto entra pelo nome, código e preço. A
              descrição é texto de vitrine, e barrar o cadastro por ela travava
              a entrada de mercadoria no balcão. */}
          <Field label="Descrição" span hint="Opcional — aparece na página do produto na loja. Use negrito, títulos e listas para a ficha do produto.">
            <RichText value={form.description} onChange={(html) => setForm((f) => ({ ...f, description: html }))} testId="product-form-description" />
          </Field>
          {/* Só o link: a loja extrai o id e monta o player. Fica antes do SEO
              porque é conteúdo do produto, não metadado — a pessoa que cadastra
              a cadeira pensa "tem vídeo?" junto com "tem foto?". */}
          <Field label="Vídeo do produto (YouTube)" span
            hint="Cole o link do YouTube — da barra do navegador, do botão Compartilhar ou do app. Aparece na página do produto, abaixo das fotos.">
            <Input value={form.video_url || ""} onChange={set("video_url")} placeholder="https://www.youtube.com/watch?v=…" inputMode="url" data-testid="product-form-video-url" />
          </Field>
          <Field label="SEO — título">
            <Input value={form.seo_title || ""} onChange={set("seo_title")} placeholder="(opcional)" data-testid="product-form-seo-title" />
          </Field>
          <Field label="SEO — descrição">
            <Input value={form.seo_description || ""} onChange={set("seo_description")} placeholder="(opcional)" data-testid="product-form-seo-desc" />
          </Field>
          {/* A galeria tem vários controles próprios (enviar, reordenar, definir
              capa, remover): o rótulo nomeia o conjunto, por isso fieldset. */}
          <fieldset className="col-span-2">
            <legend className={LEGEND}>
              Fotos do produto<span className="text-red-500 ml-0.5" aria-hidden>*</span><span className="sr-only"> (obrigatório)</span>
            </legend>
            <div className="mt-1.5">
              <ProductImages value={form.images} onChange={(images) => setForm({ ...form, images })} />
            </div>
          </fieldset>
          <Checkbox checked={form.featured} onChange={set("featured")} data-testid="product-form-featured" label="Destaque na home" />
          <Checkbox checked={form.active} onChange={set("active")} data-testid="product-form-active" label="Ativo na loja" />
          {/* Some do catálogo de venda? Não: alugável e vendável convivem —
              a mesma cadeira de rodas sai por diária e sai vendida. Isto só
              diz que ela PODE aparecer na busca de um contrato de locação. */}
          <Checkbox checked={form.rentable} onChange={set("rentable")} data-testid="product-form-rentable" label="Disponível para aluguel" />
        </div>
        {/* Linhas repetidas de variação: o rótulo do grupo é <legend> (primeiro
            filho do fieldset, senão não nomeia nada), e cada campo diz no
            próprio aria-label qual variação e qual coluna é. */}
        <fieldset className="mt-5">
          <legend className={`${LEGEND} mb-2`}>Variações &amp; Estoque</legend>
          <p className="text-[11px] text-slate-400 mb-2 -mt-1">
            A ordem daqui é a que aparece no PDV, na loja e na etiqueta.
          </p>
          <div className="grid grid-cols-12 gap-2 mb-1 px-0.5 text-[10px] font-semibold uppercase text-slate-400">
            <span className="col-span-4">Variação<span className="text-red-500 ml-0.5" title="Obrigatório">*</span></span>
            <span className="col-span-2 text-center">Estoque</span><span className="col-span-2 text-center">+R$</span>
            <span className="col-span-4 text-right">Ordem · remover</span>
          </div>
          {form.variants.map((v, i) => (
            <div key={i} className="mb-2 border border-slate-100 rounded-lg p-2">
              {/* Grade com cabeçalho de coluna acima: o nome acessível de cada
                  campo precisa dizer QUAL variação e QUAL coluna, senão o leitor
                  de tela anuncia seis campos idênticos por linha. */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} placeholder="Único, Tamanho M..."
                  aria-label={`Nome da variação ${i + 1}`} required data-testid={`variant-label-${i}`}
                  size="sm" className="col-span-4 px-2" />
                {/* Um campo de estoque: eram dois (online e física) mais o total
                    calculado, para descrever a mesma prateleira. */}
                <Input type="number" min="0" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)}
                  aria-label={`Estoque da variação ${i + 1}`} data-testid={`variant-stock-${i}`}
                  size="sm" mono className="col-span-2 px-2 text-center" />
                <Input type="number" step="0.01" value={v.price_delta} onChange={(e) => setVariant(i, "price_delta", e.target.value)}
                  placeholder="+R$" aria-label={`Acréscimo de preço da variação ${i + 1}`} data-testid={`variant-delta-${i}`}
                  size="sm" mono className="col-span-2 px-2" />
                {/* Subir/descer em vez de arrastar: a lista tem duas ou três
                    linhas e vive dentro de um modal que já rola — arrastar aqui
                    briga com a rolagem e não funciona no teclado. O nome
                    acessível diz QUAL variação move, senão o leitor de tela
                    anuncia três "subir" iguais. */}
                <div className="col-span-4 flex items-center justify-end gap-0.5">
                  <Button variant="ghost" size="iconSm" onClick={() => moverVariacao(i, i - 1)} disabled={i === 0}
                    aria-label={`Subir a variação ${v.label || i + 1}`} title="Subir"
                    data-testid={`variant-up-${i}`} className="hover:text-brand-600"><ChevronUp size={15} /></Button>
                  <Button variant="ghost" size="iconSm" onClick={() => moverVariacao(i, i + 1)} disabled={i === form.variants.length - 1}
                    aria-label={`Descer a variação ${v.label || i + 1}`} title="Descer"
                    data-testid={`variant-down-${i}`} className="hover:text-brand-600"><ChevronDown size={15} /></Button>
                  <Button variant="ghost" size="iconSm" onClick={() => removerVariacao(i)}
                    aria-label={`Remover variação ${i + 1}`} title="Remover"
                    className="hover:text-red-600"><Trash2 size={14} /></Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input value={v.barcode || ""} onChange={(e) => setVariant(i, "barcode", e.target.value)} inputMode="numeric"
                  placeholder="Código de barras (EAN) — usado no PDV; opcional"
                  aria-label={`Código de barras da variação ${i + 1}`} data-testid={`variant-barcode-${i}`}
                  size="sm" mono className="flex-1 px-2" />
                <Button variant="secondary" size="sm" icon={Camera} onClick={() => setScanFor(i)}
                  data-testid={`variant-scan-${i}`} className="shrink-0">Escanear</Button>
              </div>
            </div>
          ))}
          {/* Adicionar fica DEPOIS da lista: o <legend> precisa ser o primeiro
              filho do fieldset para nomear o grupo. */}
          <Button variant="link" size="sm" icon={Plus}
            onClick={() => setForm({ ...form, variants: [...form.variants, { label: "", stock: 0, price_delta: 0, barcode: "" }] })}
            data-testid="product-form-add-variant">Adicionar variação</Button>
        </fieldset>

        <details className="mt-5">
          <summary className="cursor-pointer text-xs font-semibold tracking-wider uppercase text-slate-500 select-none">Dados fiscais · dimensões de envio</summary>
          {/* MERCADORIA — descreve o produto, não a operação. Vale igual para
              quem compra e para quem vende, e por isso é campo único. */}
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-3">Mercadoria</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            <Field label="NCM"><Input value={form.ncm || ""} onChange={set("ncm")} data-testid="product-form-ncm" /></Field>
            <Field label="CEST"><Input value={form.cest || ""} onChange={set("cest")} /></Field>
            <Field label="Origem"><Input value={form.origem || ""} onChange={set("origem")} placeholder="0–8" /></Field>
            <Field label="Unidade"><Input value={form.unidade || ""} onChange={set("unidade")} placeholder="UN, PC..." /></Field>
            <Field label="EAN / GTIN"><Input value={form.ean || ""} onChange={set("ean")} /></Field>
          </div>

          {/* ENTRADA — como a mercadoria chegou.
              A importação de XML preenche sozinha, mas produto cadastrado à mão
              nunca passa por ela: sem campo, não havia onde registrar o que veio
              na nota de compra. Vem ANTES da venda porque é a ordem real dos
              fatos — a mercadoria entra, depois sai. */}
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-4">
            Entrada · como veio na nota de compra
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Preenchido pela importação de XML, ou à mão. É <b>referência</b>: nada daqui vai para a
            nota de venda — CFOP de compra não existe como saída.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2" data-testid="product-form-fiscal-entrada">
            <Field label="CFOP"><Input value={form.cfop_entrada || ""} onChange={set("cfop_entrada")} placeholder="2102" data-testid="product-form-cfop-entrada" /></Field>
            <Field label="CST ICMS"><Input value={form.icms_cst_entrada || ""} onChange={set("icms_cst_entrada")} /></Field>
            <Field label="Alíq. ICMS (%)"><Input type="number" step="0.01" value={form.icms_aliquota_entrada ?? ""} onChange={set("icms_aliquota_entrada")} /></Field>
            <div />
            <Field label="CST PIS"><Input value={form.pis_cst_entrada || ""} onChange={set("pis_cst_entrada")} /></Field>
            <Field label="Alíq. PIS (%)"><Input type="number" step="0.01" value={form.pis_aliquota_entrada ?? ""} onChange={set("pis_aliquota_entrada")} /></Field>
            <Field label="CST COFINS"><Input value={form.cofins_cst_entrada || ""} onChange={set("cofins_cst_entrada")} /></Field>
            <Field label="Alíq. COFINS (%)"><Input type="number" step="0.01" value={form.cofins_aliquota_entrada ?? ""} onChange={set("cofins_aliquota_entrada")} /></Field>
          </div>

          {/* SAÍDA — o que vai para a nota de venda.
              Antes não existia na tela: só a importação de XML escrevia aqui, e
              com dados do FORNECEDOR. Vazio continua sendo o caso normal (cai no
              padrão das Configurações); preencher é para o produto que foge à
              regra, como substituição tributária. */}
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-4">
            Venda · o que sai na nota
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Em branco usa o padrão das <b>Configurações fiscais</b>. O CFOP aqui é o de venda
            <b> dentro do estado</b> — para outra UF o primeiro dígito é ajustado na emissão (5→6).
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2" data-testid="product-form-fiscal-saida">
            <Field label="CFOP"><Input value={form.cfop || ""} onChange={set("cfop")} placeholder="5102" data-testid="product-form-cfop" /></Field>
            <Field label="CST ICMS"><Input value={form.icms_cst || ""} onChange={set("icms_cst")} placeholder="102" /></Field>
            <Field label="Alíq. ICMS (%)"><Input type="number" step="0.01" value={form.icms_aliquota ?? ""} onChange={set("icms_aliquota")} /></Field>
            <div />
            <Field label="CST PIS"><Input value={form.pis_cst || ""} onChange={set("pis_cst")} placeholder="49" /></Field>
            <Field label="Alíq. PIS (%)"><Input type="number" step="0.01" value={form.pis_aliquota ?? ""} onChange={set("pis_aliquota")} /></Field>
            <Field label="CST COFINS"><Input value={form.cofins_cst || ""} onChange={set("cofins_cst")} placeholder="49" /></Field>
            <Field label="Alíq. COFINS (%)"><Input type="number" step="0.01" value={form.cofins_aliquota ?? ""} onChange={set("cofins_aliquota")} /></Field>
          </div>

          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-4">Dimensões de envio</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            <Field label="Peso (kg)"><Input type="number" step="0.001" value={form.weight || ""} onChange={set("weight")} data-testid="product-form-weight" /></Field>
            <Field label="Altura (cm)"><Input type="number" step="0.01" value={form.height || ""} onChange={set("height")} /></Field>
            <Field label="Largura (cm)"><Input type="number" step="0.01" value={form.width || ""} onChange={set("width")} /></Field>
            <Field label="Compr. (cm)"><Input type="number" step="0.01" value={form.length || ""} onChange={set("length")} /></Field>
          </div>
        </details>

    </Modal>
    {scanFor !== null && (
      <BarcodeScanner
        onDetect={(code) => { setVariant(scanFor, "barcode", String(code).replace(/\D/g, "")); toast.success("Código de barras lido"); setScanFor(null); }}
        onClose={() => setScanFor(null)}
      />
    )}
    {/* A segunda confirmação. O texto diz o que de fato acontece — a variação
        sai do catálogo e a venda antiga continua de pé —, porque o ícone de
        lixeira promete outra coisa e é a diferença entre as duas que faz a
        pessoa clicar sem saber. */}
    <ConfirmDialog
      open={!!removendoVariacao}
      onClose={() => setRemovendoVariacao(null)}
      onConfirm={() => {
        tirarDaLista(removendoVariacao.i);
        setConfirmouHistorico(true);
        setRemovendoVariacao(null);
      }}
      title={`Remover a variação ${removendoVariacao?.label || ""}?`}
      message={`Esta variação já tem venda ou movimentação de estoque. Ela sai da loja, do balcão e deste cadastro, `
        + `mas as vendas antigas continuam apontando para ela — o histórico e as notas emitidas não mudam. `
        + `O código de barras volta a ficar livre para outro cadastro. A remoção vale quando você salvar o produto.`}
      confirmLabel="Remover mesmo assim"
    />
    </>
  );
}
